import React, { useMemo, useState } from "react";

import { TemplateForm } from "pages/Course/Documents/CourseDocumentForm";
import { CourseStudentDocument, courseStudentsApi } from "api/courseStudentsApi";
import { CourseDocumentApi } from "api";
import { COURSE_DOCUMENT_FILE_STATUS } from "consts";
import { useCourseDocumentsOptions } from "api/hooks";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { appendFileToBrowser } from "utils";
import { EmptyState } from "components/molecules";

interface DownloadCourseDocumentsModalProps {
  onCloseRequest: (refresh?: boolean) => void;
  courseId: number;
  isOpen: boolean;
  students: CourseStudentDocument[];
}

type DownloadMode = "single" | "zip" | "pdf-merge";

export const DownloadCourseDocumentsModal = ({
  onCloseRequest,
  isOpen,
  courseId,
  students,
}: DownloadCourseDocumentsModalProps) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [courseDocument, setCourseDocument] = React.useState<CourseDocumentApi | null>(null);
  const [downloadMode, setDownloadMode] = useState<DownloadMode>("single");
  const [customFilename, setCustomFilename] = useState("");
  const { options: courseDocumentOptions, loading: loadingOptions } = useCourseDocumentsOptions(Number(courseId));

  const studentDocuments = useMemo(
    () =>
      students.filter((s) =>
        s.documents.some(
          (d) =>
            d.courseDocumentId == courseDocument?.id && d.status == COURSE_DOCUMENT_FILE_STATUS.SuccessfullyGenerated,
        ),
      ),
    [students, courseDocument],
  );

  // Get all successfully generated document file IDs for the selected course document
  const availableDocumentFileIds = useMemo(() => {
    if (!courseDocument) return [];

    return studentDocuments
      .map(
        (student) =>
          student.documents.find(
            (doc) =>
              doc.courseDocumentId === courseDocument.id &&
              doc.status === COURSE_DOCUMENT_FILE_STATUS.SuccessfullyGenerated,
          )?.courseDocumentFileId,
      )
      .filter((id): id is number => id !== undefined);
  }, [studentDocuments, courseDocument]);

  const getMaxDocuments = () => {
    switch (downloadMode) {
      case "zip":
        return 50;
      case "pdf-merge":
        return 20;
      default:
        return Infinity;
    }
  };

  const isDocumentLimitExceeded = () => {
    const maxDocs = getMaxDocuments();
    return availableDocumentFileIds.length > maxDocs;
  };

  const generateFilename = () => {
    if (customFilename.trim()) return customFilename.trim();

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
    const baseName = courseDocument?.name || "documents";

    switch (downloadMode) {
      case "zip":
        return `${baseName}_${timestamp}.zip`;
      case "pdf-merge":
        return `${baseName}_merged_${timestamp}.pdf`;
      default:
        return `${baseName}.zip`;
    }
  };

  const onDownloadClick = async () => {
    if (!courseDocument || availableDocumentFileIds.length === 0) return;

    setSubmitting(true);
    try {
      const filename = generateFilename();

      switch (downloadMode) {
        case "single":
          // Use existing zipFiles method for single document type
          const body = { students: students.map((x) => x.id) };
          const response = await courseStudentsApi.zipFiles(Number(courseId), Number(courseDocument.id), body);
          appendFileToBrowser(response.data, `${courseDocument.name}.zip`);
          break;

        case "zip":
          const zipResponse = await courseStudentsApi.zipDownload(Number(courseId), {
            courseDocumentFileIds: availableDocumentFileIds,
            filename: filename,
          });
          appendFileToBrowser(zipResponse.data, filename);
          break;

        case "pdf-merge":
          const pdfResponse = await courseStudentsApi.pdfMerge(Number(courseId), {
            courseDocumentFileIds: availableDocumentFileIds,
            filename: filename,
          });
          appendFileToBrowser(pdfResponse.data, filename);
          break;
      }

      onCloseRequest(true);
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show an error message to the user
    } finally {
      setSubmitting(false);
    }
  };

  const getDownloadButtonText = () => {
    switch (downloadMode) {
      case "single":
        return "Pobierz ZIP";
      case "zip":
        return "Pobierz jako ZIP";
      case "pdf-merge":
        return "Scal i pobierz PDF";
      default:
        return "Pobierz";
    }
  };

  const getModalTitle = () => {
    switch (downloadMode) {
      case "single":
        return "Pobieranie dokumentów";
      case "zip":
        return "Pobieranie jako ZIP";
      case "pdf-merge":
        return "Scalanie do PDF";
      default:
        return "Pobieranie dokumentów";
    }
  };

  return (
    <PaziaModal
      id="download-modal"
      onCloseRequest={() => {
        onCloseRequest();
      }}
      isOpen={isOpen}
      style={{ maxWidth: "calc(100% - 18rem)", height: "calc(100% - 10rem)" }}
    >
      {isOpen && (
        <div className="flex h-full min-h-[20rem] flex-col px-5 pb-6 pt-4">
          {loadingOptions ? (
            <PaziaLoader position="center" />
          ) : (
            <>
              <div className="flex justify-between text-xl">
                <div className="mb-2 font-medium text-slate-800 dark:text-slate-100">{getModalTitle()}</div>
                <PaziaButton
                  type="success"
                  title={getDownloadButtonText()}
                  disabled={studentDocuments.length < 1 || isDocumentLimitExceeded()}
                  processing={submitting}
                  onClick={onDownloadClick}
                />
              </div>

              {/* Download Mode Selection */}
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Tryb pobierania
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDownloadMode("single")}
                    className={`rounded px-3 py-2 text-sm ${
                      downloadMode === "single"
                        ? "bg-blue-500 text-white"
                        : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                  >
                    Pojedynczy typ
                  </button>
                  <button
                    type="button"
                    onClick={() => setDownloadMode("zip")}
                    className={`rounded px-3 py-2 text-sm ${
                      downloadMode === "zip"
                        ? "bg-blue-500 text-white"
                        : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                  >
                    Archiwum ZIP (max 50)
                  </button>
                  <button
                    type="button"
                    onClick={() => setDownloadMode("pdf-merge")}
                    className={`rounded px-3 py-2 text-sm ${
                      downloadMode === "pdf-merge"
                        ? "bg-blue-500 text-white"
                        : "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300"
                    }`}
                  >
                    Scalanie PDF (max 20)
                  </button>
                </div>
              </div>

              {/* Custom Filename Input */}
              {(downloadMode === "zip" || downloadMode === "pdf-merge") && (
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Nazwa pliku (opcjonalnie)
                  </label>
                  <input
                    type="text"
                    value={customFilename}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomFilename(e.target.value)}
                    placeholder={`Automatyczna: ${generateFilename()}`}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  />
                </div>
              )}

              <div>
                <TemplateForm
                  courseDocumentOptions={courseDocumentOptions}
                  onChange={(x) => setCourseDocument(x?.courseDocument)}
                />
              </div>

              {/* Warning for document limits */}
              {isDocumentLimitExceeded() && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300">
                  <strong>Uwaga:</strong> Wybrano {availableDocumentFileIds.length} dokumentów, ale limit dla{" "}
                  {downloadMode === "zip" ? "archiwum ZIP" : "scalania PDF"} to {getMaxDocuments()} dokumentów.
                </div>
              )}

              {courseDocument == null ? (
                <EmptyState
                  title="Nie wybrano"
                  description="Wybierz rodzaj dokumentu do pobrania, aby podejrzeć listę plików."
                />
              ) : studentDocuments.length > 0 ? (
                <div className="flex h-[86%] flex-col overflow-y-scroll py-3">
                  <header className="rounded-sm bg-slate-50 p-2 text-xs font-semibold uppercase text-slate-400 dark:bg-slate-700 dark:bg-opacity-50 dark:text-slate-500">
                    Lista plików ({studentDocuments.length})
                  </header>
                  <ul className="my-1">
                    {studentDocuments.map((x) => (
                      <li className="flex px-2" key={x.id}>
                        <div className="flex grow items-center border-b border-slate-100 py-2 text-sm dark:border-slate-700">
                          <div className="flex grow justify-between">
                            <div className="self-center">
                              {x.firstName} {x.surname}
                            </div>
                            <div className="ml-2 shrink-0 self-start">
                              <span className="font-medium text-slate-800 dark:text-slate-100">
                                {x.documents.find((x) => x.courseDocumentId == courseDocument.id)?.fileName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <EmptyState title="Brak dokumentów" description="Wygeneruj pliki aby je pobrać." />
              )}
            </>
          )}
        </div>
      )}
    </PaziaModal>
  );
};
